/**
 * Ochiq ariza formasi (eskiz 27): driver telefonda to'ldiradi, login yo'q.
 * Ariza bazaga New bo'lib filial pooliga tushadi; SSN so'ralmaydi.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ensureCsrf } from "@/features/auth/hooks";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Field, Input, Select } from "@/components/ui/field";
import { Spinner } from "@/components/ui/misc";
import { BrandLockup } from "@/components/brand/BrandLogo";

interface ApplyConfig {
  enabled: boolean;
  company_name: string;
  states: string[];
  equipment: string[];
}

interface FormState {
  full_name: string;
  phone: string;
  email: string;
  state: string;
  cdl_class: "" | "A" | "B" | "C";
  experience: string;
  equipment: string[];
  consent: boolean;
  website: string; // honeypot: odam uchun ko'rinmaydi, botlar to'ldiradi
}

type Errors = Partial<Record<keyof FormState | "form", string>>;

// value — serverga yuboriladigan matn (inglizcha, o'zgarmas); label — tarjima kaliti apply:exp.<label>
const EXPERIENCE: { value: string; label: string }[] = [
  { value: "Less than 1 year", label: "lt1" },
  { value: "1–2 years", label: "y1_2" },
  { value: "2–5 years", label: "y2_5" },
  { value: "5+ years", label: "y5" },
];
const EMPTY: FormState = { full_name: "", phone: "", email: "", state: "", cdl_class: "", experience: "", equipment: [], consent: false, website: "" };

type ErrorKey = "name" | "nameTwo" | "phone" | "email" | "state" | "cdl" | "experience" | "consent";

/** Tekshiruv: natija — xato kalitlari (apply:errors.<key>), tarjima ko'rsatishda qilinadi */
function validate(f: FormState): Partial<Record<keyof FormState, ErrorKey>> {
  const errors: Partial<Record<keyof FormState, ErrorKey>> = {};
  const name = f.full_name.trim();
  if (name.length < 2) errors.full_name = "name";
  else if (name.split(/\s+/).length < 2) errors.full_name = "nameTwo";
  if (f.phone.replace(/\D/g, "").length < 10) errors.phone = "phone";
  if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) errors.email = "email";
  if (!f.state) errors.state = "state";
  if (!f.cdl_class) errors.cdl_class = "cdl";
  if (!f.experience) errors.experience = "experience";
  if (!f.consent) errors.consent = "consent";
  return errors;
}

export function ApplyPage() {
  const { t } = useTranslation("apply");
  const { t: tc } = useTranslation();
  const config = useQuery({
    queryKey: ["apply", "config"],
    queryFn: () => api.get<ApplyConfig>("/api/apply/config/"),
    staleTime: 10 * 60_000,
    retry: 1,
  });
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [doneName, setDoneName] = useState<string | null>(null);

  useEffect(() => {
    ensureCsrf();
  }, []);

  const company = config.data?.company_name ?? tc("app");

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function toggleEquipment(item: string) {
    set("equipment", form.equipment.includes(item) ? form.equipment.filter((e) => e !== item) : [...form.equipment, item]);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const keys = validate(form);
    const errs: Errors = {};
    for (const [k, v] of Object.entries(keys)) errs[k as keyof FormState] = t(`errors.${v}`);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setBusy(true);
    try {
      await api.post("/api/apply/", {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        state: form.state,
        cdl_class: form.cdl_class,
        experience: form.experience,
        equipment: form.equipment,
        consent: form.consent,
        website: form.website,
      });
      setDoneName(form.full_name.trim().split(/\s+/)[0] ?? "");
    } catch (err) {
      if (err instanceof ApiError && err.fields) {
        const next: Errors = {};
        for (const [key, msgs] of Object.entries(err.fields)) next[key as keyof FormState] = msgs.join(" ");
        if (Object.keys(next).length === 0) next.form = err.message;
        setErrors(next);
      } else {
        setErrors({ form: err instanceof ApiError ? err.message : t("sendFailed") });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen justify-center bg-canvas px-4 py-6 sm:py-10">
      <div className="w-full max-w-[420px]">
        <div className="rounded-card border border-border/60 bg-surface px-5 py-6 shadow-card sm:px-7">
          <div className="mb-5 flex items-center justify-between gap-2">
            <BrandLockup height={24} />
            <div className="flex items-center gap-2">
              <div className="text-[11px] text-muted">{t("brand")}</div>
              <LanguageSwitcher persist={false} />
            </div>
          </div>

          {config.isPending ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : config.data && !config.data.enabled ? (
            <div className="py-8 text-center">
              <div className="text-[17px] font-bold">{t("closedTitle")}</div>
              <p className="mt-2 text-[13px] text-muted">{t("closedDesc")}</p>
            </div>
          ) : doneName !== null ? (
            <ThankYou name={doneName} company={company} onReset={() => { setForm(EMPTY); setErrors({}); setDoneName(null); }} />
          ) : (
            <form onSubmit={submit} noValidate className="space-y-4">
              <div>
                <h1 className="text-[20px] leading-tight font-bold">{t("h1")}</h1>
                <p className="mt-1 text-[12px] text-muted">{t("lead")}</p>
              </div>
              {errors.form && (
                <div role="alert" className="rounded-[10px] border border-danger bg-danger-soft px-3 py-2 text-[12px] text-danger">
                  {errors.form}
                </div>
              )}
              <Field label={t("fullName")} required error={errors.full_name} htmlFor="full_name">
                <Input id="full_name" autoComplete="name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Daniel Clark" aria-invalid={!!errors.full_name} />
              </Field>
              <Field label={t("phone")} required error={errors.phone} htmlFor="phone">
                <Input id="phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 010-0190" aria-invalid={!!errors.phone} />
              </Field>
              <Field label={t("email")} error={errors.email} htmlFor="email">
                <Input id="email" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="daniel.c@example.com" aria-invalid={!!errors.email} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("state")} required error={errors.state} htmlFor="state">
                  <Select id="state" value={form.state} onChange={(e) => set("state", e.target.value)} aria-invalid={!!errors.state}>
                    <option value="">{t("statePlaceholder")}</option>
                    {(config.data?.states ?? []).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={t("cdlClass")} required error={errors.cdl_class} htmlFor="cdl_class">
                  <Select id="cdl_class" value={form.cdl_class} onChange={(e) => set("cdl_class", e.target.value as FormState["cdl_class"])} aria-invalid={!!errors.cdl_class}>
                    <option value="">{t("classPlaceholder")}</option>
                    <option value="A">{t("classA")}</option>
                    <option value="B">{t("classB")}</option>
                    <option value="C">{t("classC")}</option>
                  </Select>
                </Field>
              </div>
              <Field label={t("experience")} required error={errors.experience} htmlFor="experience">
                <Select id="experience" value={form.experience} onChange={(e) => set("experience", e.target.value)} aria-invalid={!!errors.experience}>
                  <option value="">{t("choose")}</option>
                  {EXPERIENCE.map((x) => (
                    <option key={x.value} value={x.value}>
                      {t(`exp.${x.label}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <div>
                <div className="mb-1 text-[12px] text-ink-soft">{t("equipment")}</div>
                <div className="flex flex-wrap gap-2">
                  {(config.data?.equipment ?? ["Dry van", "Reefer", "Flatbed", "Tanker"]).map((item) => {
                    const on = form.equipment.includes(item);
                    return (
                      <button
                        key={item}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleEquipment(item)}
                        className={cn(
                          "rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors",
                          on ? "border-primary bg-primary text-white" : "border-border bg-fill text-ink hover:bg-fill-hover",
                        )}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Honeypot: ko'rinmaydi, to'ldirilsa bot deb hisoblanadi */}
              <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
                <label>
                  {t("website")}
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} />
                </label>
              </div>
              <div>
                <label className={cn("flex cursor-pointer items-start gap-2 text-[11px] leading-snug text-ink-soft", errors.consent && "text-danger")}>
                  <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} aria-invalid={!!errors.consent} />
                  <span>{t("consent")}</span>
                </label>
                {errors.consent && <p className="mt-1 text-[12px] text-danger">{errors.consent}</p>}
              </div>
              <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
                {t("submit")}
              </Button>
              <p className="text-center text-[11px] text-muted">{t("noSsn")}</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function ThankYou({ name, company, onReset }: { name: string; company: string; onReset: () => void }) {
  const { t } = useTranslation("apply");
  return (
    <div className="py-6 text-center">
      <CheckCircle2 size={56} className="mx-auto text-success" aria-hidden="true" />
      <h1 className="mt-4 text-[22px] font-bold">{name ? t("thanks", { name }) : t("thanksAnon")}</h1>
      <p className="mx-auto mt-2 max-w-[300px] text-[13px] text-ink-soft">{t("received")}</p>
      <p className="mt-4 text-[11px] text-muted">{t("questions")}</p>
      <Button variant="secondary" size="lg" className="mt-8 w-full" onClick={onReset}>
        {t("back", { company })}
      </Button>
    </div>
  );
}
